/**
 * Per voice-loop iter 4 — `audio_prewarm` flag.
 *
 * Browsers (Chrome / Safari especially) charge a 50-150ms tax the
 * first time an <audio> element loads + decodes a fresh blob, AND
 * may refuse `play()` entirely if it's not within a user gesture
 * stack. Both penalties cluster on the user's first chat turn —
 * where they're most likely to bail.
 *
 * Trick: when the chat page mounts (which IS inside the user-gesture
 * stack — they clicked 開始聊天 to navigate here), play a 100ms
 * silent WAV through the same <audio> we'll use for TTS. The element
 * is now "warm" — codec initialised, autoplay credit banked — so
 * the real first chunk plays cleanly.
 *
 * Side-effect-free if it fails (autoplay blocked, no user gesture
 * after all, etc.) — we just log and bail.
 */

/** ~100ms of silence as a minimal PCM-16 mono WAV blob. */
function silentWavBlob(): Blob {
  const sampleRate = 8000;
  const samples = 800; // 100 ms
  const headerSize = 44;
  const buf = new ArrayBuffer(headerSize + samples);
  const view = new DataView(buf);
  // "RIFF" + chunk size + "WAVE"
  view.setUint8(0, 0x52); view.setUint8(1, 0x49); view.setUint8(2, 0x46); view.setUint8(3, 0x46);
  view.setUint32(4, 36 + samples, true);
  view.setUint8(8, 0x57); view.setUint8(9, 0x41); view.setUint8(10, 0x56); view.setUint8(11, 0x45);
  // "fmt " chunk
  view.setUint8(12, 0x66); view.setUint8(13, 0x6d); view.setUint8(14, 0x74); view.setUint8(15, 0x20);
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);  // PCM
  view.setUint16(22, 1, true);  // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true);
  view.setUint16(32, 1, true);  // block align
  view.setUint16(34, 8, true);  // 8-bit
  // "data" chunk
  view.setUint8(36, 0x64); view.setUint8(37, 0x61); view.setUint8(38, 0x74); view.setUint8(39, 0x61);
  view.setUint32(40, samples, true);
  // 0x80 = silence midpoint for unsigned 8-bit PCM
  for (let i = 0; i < samples; i++) view.setUint8(headerSize + i, 0x80);
  return new Blob([buf], { type: 'audio/wav' });
}

/**
 * Fire-and-forget prewarm. Always resolves — never rejects — so
 * callers can `void prewarmAudioElement(el)` without try/catch.
 *
 * The element MUST already be in the DOM (so it has an audio context
 * routed to the output) and SHOULD be muted=false; the prewarm tones
 * are below audible threshold anyway. Resolves once the silent buffer
 * has been played + paused, after which the element is "warm".
 */
export async function prewarmAudioElement(el: HTMLAudioElement): Promise<void> {
  try {
    const blob = silentWavBlob();
    const url = URL.createObjectURL(blob);
    // Don't clobber a real src — if something is already loaded,
    // skip. Realistically prewarm fires on mount before any real
    // playback, so el.src should be empty.
    const hadSrc = !!el.src && el.src !== 'about:blank';
    if (hadSrc) {
      URL.revokeObjectURL(url);
      return;
    }
    el.src = url;
    try {
      const p = el.play();
      if (p && typeof p.then === 'function') {
        await p;
      }
      el.pause();
      el.currentTime = 0;
    } catch (err) {
      // Autoplay blocked / no user gesture — silent fail. The real
      // first TTS chunk will still work; we just don't get the
      // codec-init speedup.
      console.log('[audio-prewarm] play() refused, skipping', (err as Error).message);
    } finally {
      try { URL.revokeObjectURL(url); } catch { /* noop */ }
      // Clear src so the next real src= assignment isn't compared
      // against a now-dead blob URL.
      try { el.removeAttribute('src'); el.load(); } catch { /* noop */ }
    }
  } catch (err) {
    console.warn('[audio-prewarm] failed', err);
  }
}
